import React from 'react';
import { Box, Typography, CircularProgress, Tabs, Tab, Paper, Avatar, Chip, Stack, IconButton, Tooltip, Button } from '@mui/material';
import { Person as PersonIcon, Work as WorkIcon, Event as EventIcon, Folder as FolderIcon, ArrowBack as ArrowBackIcon, RequestQuote as SalaryIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import PersonDocuments from './PersonDocuments';
import { peopleService, employeeService, contractService, offerService } from '../../services/api';
import ContractForm from '../ContractForm';
import OfferForm from '../OfferForm';
import EmployeeForm from '../EmployeeForm';
import CVUpload from '../CVUpload';
import { CareerEventsTimeline } from '../CareerEvents';
import { usePermissions } from '../PermissionsContext';
import ContractSalaryTab from '../Employment/ContractSalaryTab';   // S3
import TerminateDialog from '../Employment/TerminateDialog';       // S3
import { fmtDate, isoToday } from '../Employment/salaryFormat';    // S3

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function PersonDossier({ person, summary: summaryProp, onBack }) {
  const { t } = useTranslation();
  const { role, hasPermission } = usePermissions();
  const canViewContracts = hasPermission('ViewContracts');                 // S3
  const canTerminate = hasPermission('Employee.Manage');                   // S3
  const canViewEvents = hasPermission('CareerEvents.View');                // CEV-S1
  const canManageEvents = hasPermission('CareerEvents.Manage');            // CEV-S1
  const [terminateOpen, setTerminateOpen] = React.useState(false);         // S3
  const personId = person?.Id ?? person?.id;

  const [activeTab, setActiveTab] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [summary, setSummary] = React.useState(summaryProp || null);
  const [employee, setEmployee] = React.useState(null);
  const [contract, setContract] = React.useState(null);
  const [offer, setOffer] = React.useState(null);

  const load = React.useCallback(async (createdEmployee) => {
    if (!personId) return;

    // If we just created an employee, set it immediately so the form switches to Update mode
    if (createdEmployee?.id || createdEmployee?.Id) {
      setEmployee(createdEmployee);
    }

    setLoading(true);
    try {
      const s = await peopleService.getProfileSummary(personId);
      setSummary(s || null);

      // Always try to load employee after create (flags may not be updated yet)
      let e = null;
      if (createdEmployee?.id || createdEmployee?.Id || s?.flags?.hasEmployee || s?.employee) {
        e = await employeeService.getByPerson(personId).catch(()=>null);
      }
      setEmployee(e || null);

      const contractId = s?.activeContract?.id || s?.activeContract?.Id;
      if (contractId) {
        const fullC = await contractService.getById(contractId).catch(()=>null);
        setContract(fullC || s.activeContract);
      } else {
        setContract(null);
      }

      const offerId = s?.offer?.id || s?.offer?.Id;
      if (offerId) {
        const fullO = await offerService.getById(offerId).catch(()=>null);
        setOffer(fullO || s.offer);
      } else {
        setOffer(null);
      }
    } finally {
      setLoading(false);
    }
  }, [personId]);

  React.useEffect(()=>{ load(); }, [load]);

  const currentContract = contract || summary?.activeContract || null;
  const currentOffer = offer || summary?.offer || null;
  // HRManager can only view (read-only), not edit General tab data
  const isHRManagerReadOnly = role === 'HRManager';
  const disabled = !personId || isHRManagerReadOnly;

  const fullName = person?.FullName || person?.fullName || `${(person?.FirstName || person?.firstName || '').trim()} ${(person?.LastName || person?.lastName || '').trim()}`.trim();
  const initials = fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const positionName = employee?.positionTitle || employee?.position?.title || summary?.employee?.positionTitle || '';
  const departmentName = employee?.departmentName || employee?.department?.name || summary?.employee?.departmentName || '';
  const email = employee?.email || person?.Email || person?.email || '';
  const status = employee?.status || 'Unknown';
  // S3 — a recorded future termination shows as "Leaving <date>" until the date comes.
  const leavingDate = employee?.terminationDate && String(employee.terminationDate).substring(0, 10) > isoToday()
    ? employee.terminationDate : null;

  if (!personId) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <Typography color="text.secondary">{t('dossier.selectPerson', 'Select a person to view their dossier')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* Header - Always visible */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {onBack && (
            <Tooltip title={t('dossier.backToList', 'Back to list')}>
              <IconButton onClick={onBack} size="small" sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          )}
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: '1.25rem' }}>
            {initials || <PersonIcon />}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{fullName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {[positionName, departmentName, email].filter(Boolean).join(' • ')}
            </Typography>
          </Box>
          <Chip
            label={status}
            color={status === 'Active' ? 'success' : status === 'Inactive' ? 'default' : 'warning'}
            size="small"
          />
          {leavingDate && <Chip label={`Leaving ${fmtDate(leavingDate)}`} color="warning" size="small" />}
          {employee?.id && canTerminate && (
            <Button variant="outlined" color="error" onClick={() => setTerminateOpen(true)}>
              {employee.terminationDate ? 'Termination' : 'Terminate employment'}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<WorkIcon />} iconPosition="start" label={t('dossier.tabs.general', 'General')} />
          {/* S3 — Contract & Salary; hidden without ViewContracts, keeping the other tab indexes */}
          <Tab icon={<SalaryIcon />} iconPosition="start" label={t('dossier.tabs.contractSalary', 'Contract & Salary')}
            sx={canViewContracts ? undefined : { display: 'none' }} />
          <Tab icon={<EventIcon />} iconPosition="start" label={t('dossier.tabs.careerEvents', 'Career Events')} />
          <Tab icon={<FolderIcon />} iconPosition="start" label={t('dossier.tabs.documents', 'Documents')} />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Tab 0: General */}
              <TabPanel value={activeTab} index={0}>
                {/* Employee section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {t('dossier.sections.employee', 'Employee')}
                  </Typography>
                  <EmployeeForm embedded={true} person={person} employee={employee} disabled={disabled} onSave={load} onCancel={()=>{}} />
                </Box>

                {/* Contract section — S3: contracts need ViewContracts */}
                {canViewContracts && <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {t('dossier.sections.contract', 'Contract')}
                  </Typography>
                  <ContractForm
                    key={`contract-${currentContract?.id || currentContract?.Id || 'new'}`}
                    embedded={true}
                    contractId={currentContract?.id || currentContract?.Id}
                    personId={personId}
                    disabled={disabled}
                    onSaved={load}
                    onCancel={()=>{}}
                  />
                </Box>}

                {/* Offer section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {t('dossier.sections.offer', 'Offer')}
                  </Typography>
                  <OfferForm embedded={true} offerId={currentOffer?.id || currentOffer?.Id} disabled={disabled} onSaved={load} onCancel={()=>{}} />
                </Box>

              </TabPanel>

              {/* Tab 1: Contract & Salary (S3) */}
              <TabPanel value={activeTab} index={1}>
                {canViewContracts && <ContractSalaryTab personId={personId} />}
              </TabPanel>

              {/* Tab 2: Career Events */}
              <TabPanel value={activeTab} index={2}>
                {!canViewEvents ? (
                  <Typography color="text.secondary">
                    {t('dossier.noCareerEventsAccess', 'Career events are visible to HR and the board only.')}
                  </Typography>
                ) : employee?.id ? (
                  <CareerEventsTimeline
                    employeeId={employee.id}
                    personName={fullName}
                    readOnly={!canManageEvents}
                  />
                ) : (
                  <Typography color="text.secondary">
                    {t('dossier.noEmployee', 'No employee record. Create an employee record in the General tab first.')}
                  </Typography>
                )}
              </TabPanel>

              {/* Tab 3: Documents */}
              <TabPanel value={activeTab} index={3}>
                {/* Person Documents - grouped by phase with upload/view/delete */}
                <PersonDocuments personId={personId} />

                {/* CV Upload section */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {t('dossier.sections.cvUpload', 'CV Upload & Processing')}
                  </Typography>
                  <CVUpload />
                </Box>
              </TabPanel>
            </>
          )}
        </Box>
      </Paper>

      {/* S3 — record / change / remove a termination */}
      <TerminateDialog open={terminateOpen} employee={employee} fullName={fullName}
        onClose={() => setTerminateOpen(false)}
        onSaved={() => { setTerminateOpen(false); load(); }} />
    </Box>
  );
}
